import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Avatar,
  Divider,
  Empty
} from 'antd';
import {
  GiftOutlined,
  BarsOutlined,
  TrophyOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  UserOutlined,
  RocketOutlined,
  TeamOutlined
} from '@ant-design/icons';

import { setAppTitle } from 'src/store/app';
import { profileSelector } from 'src/store/profile';
import {
  fetchGiveaways,
  giveawaysListSelector,
  giveawaysMetaDataSelector,
  isLoadingGivewaySelector
} from 'src/store/giveaway';
import { fetchCommands, commandsListSelector } from 'src/store/command';

import { ROUTE } from 'src/constants';

import './dashboard.scss';

const { Title, Text } = Typography;

const MetricCard = ({ title, value, icon, color }) => (
  <Card className="dashboard__metric-card" bordered={false}>
    <div className="dashboard__metric-card__inner">
      <div className="dashboard__metric-card__icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      <Statistic
        title={title}
        value={value}
        valueStyle={{ color, fontWeight: 700, fontSize: 26 }}
      />
    </div>
  </Card>
);

export const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profile = useSelector(profileSelector);
  const giveaways = useSelector(giveawaysListSelector);
  const giveawaysMeta = useSelector(giveawaysMetaDataSelector);
  const isLoadingGiveaways = useSelector(isLoadingGivewaySelector);
  const commands = useSelector(commandsListSelector);

  useEffect(() => {
    dispatch(setAppTitle(t('menu:dashboard')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchGiveaways({}));
    dispatch(fetchCommands());
  }, [dispatch]);

  const totalGiveaways = giveawaysMeta?.total || giveaways.length || 0;
  const activeGiveaways = giveaways.filter((g) => g.status === 'active').length;
  const completedGiveaways = giveaways.filter((g) => g.status === 'completed').length;
  const totalWinners = giveaways.reduce((acc, current) => {
    const currentWinners = current?.winners?.length || current?.winnersCount || 0;
    return acc + currentWinners;
  }, 0);
  const totalCommands = commands.length;
  const activeCommands = commands.filter((c) => c.active).length;
  const giveawayCommands = commands.filter((c) => c.isGiveawayCommand).length;
  const totalViewers =
    profile?.totalViewers ||
    profile?.followersCount ||
    profile?.viewersCount ||
    profile?.viewerCount ||
    0;
  const activeViewers =
    profile?.activeViewers || profile?.onlineViewers || profile?.chattersCount || 0;
  const followerParticipation = profile?.followerParticipationRate || 0;

  const recentGiveaways = giveaways.slice(0, 5);
  const recentWinners = giveaways
    .flatMap((item) => item?.winners || [])
    .slice(0, 6)
    .map((item, index) => ({
      key: `${item?._id || 'winner'}-${index}`,
      nickname: item?.entryId?.nickname || item?.viewerId?.login || item?.nickname || 'Unknown',
      ticketNumber: item?.entryId?.ticketNumber || item?.ticketNumber || '-',
      isFollower: item?.viewerId?.isFollower ?? null
    }));

  const identityName = profile?.displayName || profile?.login || 'Streamer';

  const giveawayColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const colorMap = { active: 'green', completed: 'blue', inactive: 'default' };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      }
    },
    {
      title: 'Winners',
      dataIndex: 'winnersCount',
      key: 'winnersCount',
      width: 90,
      align: 'center'
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard__content">
        <div className="dashboard__welcome">
          <Space align="center" size={16}>
            <Avatar
              size={52}
              src={profile?.profileImageUrl}
              icon={<UserOutlined />}
              className="dashboard__welcome-avatar"
            />
            <div>
              <Title level={4} className="dashboard__welcome-title">
                Welcome back, {identityName}!
              </Title>
              <Space size={6} className="dashboard__welcome-tags">
                {profile?.isLive ? <Tag color="green">🔴 Live</Tag> : <Tag>Offline</Tag>}
                {profile?.isBotEnabled ? (
                  <Tag color="purple">Bot active</Tag>
                ) : (
                  <Tag color="red">Bot disabled</Tag>
                )}
              </Space>
            </div>
          </Space>
        </div>

        {/* Metric cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Total Giveaways"
              value={totalGiveaways}
              icon={<GiftOutlined />}
              color="#874ae2"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Total Viewers"
              value={totalViewers}
              icon={<TeamOutlined />}
              color="#722ed1"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Active Viewers"
              value={activeViewers}
              icon={<ThunderboltOutlined />}
              color="#52c41a"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Active Giveaways"
              value={activeGiveaways}
              icon={<ThunderboltOutlined />}
              color="#52c41a"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Completed"
              value={completedGiveaways}
              icon={<CheckCircleOutlined />}
              color="#1677ff"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Total Winners"
              value={totalWinners}
              icon={<TrophyOutlined />}
              color="#faad14"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Commands"
              value={totalCommands}
              icon={<BarsOutlined />}
              color="#fa8c16"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={4}>
            <MetricCard
              title="Active Commands"
              value={activeCommands}
              icon={<TeamOutlined />}
              color="#13c2c2"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={6}>
            <MetricCard
              title="Giveaway Cmds"
              value={giveawayCommands}
              icon={<TrophyOutlined />}
              color="#eb2f96"
            />
          </Col>
          <Col xs={12} sm={8} md={8} lg={6}>
            <MetricCard
              title="Follower Participation %"
              value={followerParticipation}
              icon={<CheckCircleOutlined />}
              color="#1677ff"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card
              className="dashboard__section-card"
              bordered={false}
              title={
                <Space>
                  <GiftOutlined style={{ color: '#874ae2' }} />
                  <span>Recent Giveaways</span>
                </Space>
              }
              extra={
                <Button
                  type="link"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(ROUTE.DASHBOARD.GIVEAWAY)}
                >
                  View all
                </Button>
              }
            >
              {recentGiveaways.length > 0 ? (
                <Table
                  dataSource={recentGiveaways}
                  columns={giveawayColumns}
                  rowKey="_id"
                  pagination={false}
                  loading={isLoadingGiveaways}
                  size="small"
                />
              ) : (
                <Empty description="No giveaways yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              className="dashboard__section-card"
              bordered={false}
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#874ae2' }} />
                  <span>Recent Winners</span>
                </Space>
              }
            >
              {recentWinners.length ? (
                <div className="dashboard__winners-list">
                  {recentWinners.map((winner) => (
                    <div key={winner.key} className="dashboard__winner-item">
                      <div>
                        <Text strong>{winner.nickname}</Text>
                        <div>
                          <Text type="secondary">Ticket #{winner.ticketNumber}</Text>
                        </div>
                      </div>
                      {winner.isFollower === null ? (
                        <Tag>Unknown</Tag>
                      ) : winner.isFollower ? (
                        <Tag color="blue">Follower</Tag>
                      ) : (
                        <Tag color="default">Viewer</Tag>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No winners yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              className="dashboard__section-card"
              bordered={false}
              title={
                <Space>
                  <RocketOutlined style={{ color: '#874ae2' }} />
                  <span>Quick Actions</span>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size={10}>
                <Button
                  type="primary"
                  block
                  icon={<GiftOutlined />}
                  onClick={() => navigate(ROUTE.DASHBOARD.GIVEAWAY)}
                >
                  Manage Giveaways
                </Button>
                <Button
                  block
                  icon={<BarsOutlined />}
                  onClick={() => navigate(ROUTE.DASHBOARD.COMMANDS)}
                >
                  Manage Commands
                </Button>
                <Button
                  block
                  icon={<UserOutlined />}
                  onClick={() => navigate(ROUTE.DASHBOARD.PROFILE)}
                >
                  View Profile
                </Button>
              </Space>

              <Divider style={{ margin: '16px 0' }} />

              <Title level={5} style={{ margin: '0 0 12px 0', fontSize: 13 }}>
                Command Overview
              </Title>
              <div className="dashboard__summary">
                <div className="dashboard__summary-row">
                  <Text type="secondary">Total</Text>
                  <Text strong>{totalCommands}</Text>
                </div>
                <div className="dashboard__summary-row">
                  <Text type="secondary">Active</Text>
                  <Text strong style={{ color: '#52c41a' }}>
                    {activeCommands}
                  </Text>
                </div>
                <div className="dashboard__summary-row">
                  <Text type="secondary">Giveaway commands</Text>
                  <Text strong style={{ color: '#874ae2' }}>
                    {giveawayCommands}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card
              className="dashboard__section-card"
              bordered={false}
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#874ae2' }} />
                  <span>Activity Overview</span>
                </Space>
              }
            >
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={8}>
                  <Statistic title="Giveaways in progress" value={activeGiveaways} />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic title="Completed giveaways" value={completedGiveaways} />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic title="Winners selected" value={totalWinners} />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};
